import React, { FC, useCallback, useMemo, useState } from "react";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import { GalleryPreview } from "../gallery-preview";
import { IconButton, styled } from "@mui/material";
import { MdClose } from "react-icons/md";
import { RenderDocumentType } from "@serviestate/shared";
import { useContainerDimensions } from "../../hooks/use-container-dimensions";

export type GalleryProps = {
  photos: { title: string; resource: string; renderType: RenderDocumentType }[];
};

export const Gallery: FC<GalleryProps> = ({ photos }) => {
  const [opened, setOpened] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [startPosition, setStartPosition] = useState(0);
  const handleOpenGallary = useCallback((p?: number) => {
    setIsFirstRender(false);
    setStartPosition(p ?? 0);
    setOpened(true);
  }, []);
  const handleCloseGallery = useCallback(() => setOpened(false), []);

  return (
    <>
      <GalleryPreview
        photos={photos.filter((x) => x.renderType === "image")}
        onPhotoClick={(p) => handleOpenGallary(p)}
        onClikShowAll={() => handleOpenGallary(0)}
      />
      {!isFirstRender && (
        <FullscreenGallery
          open={opened}
          photos={photos}
          startPosition={startPosition}
          onClose={handleCloseGallery}
        />
      )}
    </>
  );
};
const FullscreenGallery: FC<{
  open: boolean;
  startPosition: number;
  photos: { title: string; resource: string; renderType: RenderDocumentType }[];
  onClose?: () => void;
}> = ({ open, startPosition, photos, onClose }) => {
  const [ref, dimensions] = useContainerDimensions();

  const images = useMemo(
    () =>
      photos.map<ReactImageGalleryItem>((photo) => ({
        original: `${photo.resource}`,
        thumbnail: `${photo.resource}?fit=cover&width=250&height=150&format=webp`,
        renderItem() {
          return <GalleryItem photo={photo} dimensions={dimensions} />;
        },
        renderThumbInner() {
          return <GalleryItem photo={photo} thumbnail />;
        },
      })),
    [photos, dimensions]
  );

  return (
    <Container open={open} ref={ref}>
      <IconButton className="close-button" title="Close" onClick={onClose}>
        <MdClose color="#fff" />
      </IconButton>
      <ImageGallery items={images} lazyLoad startIndex={startPosition} />
    </Container>
  );
};

function GalleryItem({
  photo,
  thumbnail,
  dimensions,
}: {
  photo: {
    title: string;
    resource: string;
    renderType: RenderDocumentType;
  };
  thumbnail?: boolean;
  dimensions?: {
    width: number;
    height: number;
  };
}) {
  if (dimensions && dimensions.width < 1 && dimensions.height < 1) {
    return null;
  }

  const contentUrl = !thumbnail
    ? dimensions
      ? `${photo.resource}?format=webp&width=${dimensions.width}&height=${dimensions.height}&noscaleup=true&fit=inside`
      : `${photo.resource}?format=webp`
    : `${photo.resource}?fit=cover&width=250&height=150&format=webp`;

  const imageStyle: React.CSSProperties = thumbnail
    ? {
        verticalAlign: "middle",
        lineHeight: 0,
        width: photo.renderType === "video" ? 55 : 92,
        height: 55,
      }
    : {
        maxHeight: "600px",
        maxWidth: "100%",
        objectFit: "contain",
      };

  if (photo.renderType === "video") {
    if (thumbnail) {
      return (
        <img style={imageStyle} src={"/assets/play-video.png"} loading="lazy" />
      );
    }
    return <video src={`${photo.resource}`} style={imageStyle} controls />;
  }

  return <img loading="lazy" style={imageStyle} src={contentUrl} />;
}

const Container = styled("div")<{ open: boolean }>`
  position: fixed;
  top: ${(props) => (props.open ? "0" : "-100vh")};
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #000;
  z-index: 1000;
  opacity: ${(props) => (props.open ? "1" : "0")};
  transition: opacity 0.5s;

  .photos {
    display: flex;
  }

  .close-button {
    position: absolute;
    right: 1rem;
    top: 1rem;
    z-index: 100;
    color: #fff;
  }

  .image-gallery {
    height: 100%;

    .image-gallery-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      .image-gallery-slide-wrapper {
        flex: 1;

        .image-gallery-swipe {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }
`;
